import type { SafetyPolicyType, WorkspaceRiskCatalogItem, WorkspaceRiskListItem } from "@unbox/api";
import type { MerchantPreferredLanguage } from "@unbox/db";
import { ChevronDown, ClockFastForward, InfoCircle, Stars02 } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { POLICY_NAMES } from "@/components/safety-config/policy-copy";
import { buildRiskGroupsFromCatalog, GROUP_ICONS, type RiskItem } from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";
import { SUPPORTED_RISK_EXPLANATION_LANGUAGES } from "@/utils/risk-explanation-languages";
import { handlingFor, type RiskHandling, type RiskSelection, skippedHandling } from "./risk-setup";
import { ChoiceCard, FieldRow } from "./wizard-ui";

export type HandlingMode = "now" | "skip";
export type HandlingForm = { language: MerchantPreferredLanguage; mode: HandlingMode | null; risks: RiskHandling[] };

const NO_POLICY = "none";
const POLICY_OPTIONS: { id: string; label: string }[] = [
  { id: NO_POLICY, label: "No policy" },
  ...(Object.entries(POLICY_NAMES) as [SafetyPolicyType, string][]).map(([id, label]) => ({ id, label })),
];

const itemKeys = (item: RiskItem) => item.keys ?? [item.key];

const DetectedToggle = ({ autoConfirm, onChange }: { autoConfirm: boolean; onChange: (value: boolean) => void }) => (
  <div role="radiogroup" aria-label="When risk is detected" className="flex h-[38px] rounded-lg bg-gray-100 p-1">
    {[
      { value: true, label: "Auto-approve" },
      { value: false, label: "Require review" },
    ].map((option) => (
      <button
        key={option.label}
        type="button"
        role="radio"
        aria-checked={autoConfirm === option.value}
        onClick={() => onChange(option.value)}
        className={cx(
          "flex-1 rounded-md px-3 text-sm font-semibold transition-colors duration-100 whitespace-nowrap",
          autoConfirm === option.value ? "bg-white text-primary shadow-xs" : "text-quaternary hover:text-secondary",
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const PolicyPicker = ({
  value,
  onChange,
}: {
  value: SafetyPolicyType | null;
  onChange: (value: SafetyPolicyType | null) => void;
}) => {
  const selected = value ?? NO_POLICY;
  return (
    <Dropdown.Root>
      <AriaButton
        aria-label="If risk is confirmed"
        className="flex h-[38px] w-full items-center justify-between gap-2 rounded-lg border border-primary bg-white px-3 text-sm font-medium text-primary outline-none cursor-pointer"
      >
        <span className="truncate">{POLICY_OPTIONS.find((option) => option.id === selected)?.label}</span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-quaternary" />
      </AriaButton>
      <Dropdown.Popover>
        <Dropdown.Menu
          selectedKeys={new Set([selected])}
          onSelectionChange={(keys) => {
            const next = [...keys][0];
            if (next) onChange(next === NO_POLICY ? null : (next as SafetyPolicyType));
          }}
        >
          {POLICY_OPTIONS.map((option) => (
            <Dropdown.Item key={option.id} id={option.id} textValue={option.label}>
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
};

export const resolvedHandling = (
  catalog: WorkspaceRiskCatalogItem[],
  workspaceRisks: WorkspaceRiskListItem[],
  selection: RiskSelection[],
  form: HandlingForm,
): RiskHandling[] =>
  form.mode === "skip"
    ? skippedHandling(catalog, selection)
    : handlingFor(catalog, workspaceRisks, selection, form.risks);

export const StepConfigureRisks = ({
  catalog,
  workspaceRisks,
  selection,
  value,
  onChange,
  showErrors,
}: {
  catalog: WorkspaceRiskCatalogItem[];
  workspaceRisks: WorkspaceRiskListItem[];
  selection: RiskSelection[];
  value: HandlingForm;
  onChange: (value: HandlingForm) => void;
  showErrors: boolean;
}) => {
  const hasWorkspaceSafety = workspaceRisks.length > 0;
  const rows = handlingFor(catalog, workspaceRisks, selection, value.risks);
  const byRiskId = new Map(rows.map((row) => [row.riskId, row]));
  const selectedIds = new Set(selection.map((risk) => risk.riskId));
  const groups = buildRiskGroupsFromCatalog(catalog.filter((risk) => selectedIds.has(risk.id)));

  const updateItem = (item: RiskItem, patch: Partial<RiskHandling>) => {
    const keys = new Set(itemKeys(item));
    onChange({ ...value, risks: rows.map((row) => (keys.has(row.riskId) ? { ...row, ...patch } : row)) });
  };

  const languageLabel = (code: MerchantPreferredLanguage) => {
    const language = SUPPORTED_RISK_EXPLANATION_LANGUAGES.find((l) => l.code === code);
    return language?.english ?? language?.name ?? code;
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="rounded-2xl border border-secondary bg-white px-8">
        <FieldRow
          label="Creator message language"
          required
          description={
            <>
              <p>Standard risk explanations sent to creators are translated into this language.</p>
              <p>Anything you write yourself is sent exactly as typed.</p>
            </>
          }
        >
          <Dropdown.Root>
            <AriaButton
              aria-label="Creator message language"
              className="flex h-10 w-full items-center justify-between rounded-lg border border-primary bg-white px-3.5 text-md text-primary shadow-xs outline-none cursor-pointer"
            >
              {languageLabel(value.language)}
              <ChevronDown aria-hidden="true" className="size-4 text-quaternary" />
            </AriaButton>
            <Dropdown.Popover>
              <Dropdown.Menu
                selectedKeys={new Set([value.language])}
                onSelectionChange={(keys) => {
                  const next = [...keys][0] as MerchantPreferredLanguage | undefined;
                  if (next) onChange({ ...value, language: next });
                }}
              >
                {SUPPORTED_RISK_EXPLANATION_LANGUAGES.map((language) => (
                  <Dropdown.Item key={language.code} id={language.code} textValue={languageLabel(language.code)}>
                    {language.code === "en" ? "English (default)" : languageLabel(language.code)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
          {value.language !== "en" && (
            <p className="flex gap-2 rounded-lg border border-warning-300 bg-warning-25 px-3 py-2.5 text-sm text-warning-700">
              <InfoCircle className="mt-0.5 size-4 shrink-0" />
              <span>
                This cannot be changed once defined. Any questions contact{" "}
                <a href="mailto:support@unbox-commerce.com" className="font-semibold underline">
                  support@unbox-commerce.com
                </a>
                .
              </span>
            </p>
          )}
        </FieldRow>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-md font-semibold text-primary">Choose how to handle detected risks</h2>
        <div role="radiogroup" aria-label="Choose how to handle detected risks" className="flex flex-wrap gap-4">
          <ChoiceCard
            icon={Stars02}
            title="Set up how to handle now"
            description={
              hasWorkspaceSafety
                ? "Start from the actions your brand set up previously. Change anything that doesn't fit."
                : "Start from Unbox's recommended actions. Change anything that doesn't fit."
            }
            selected={value.mode === "now"}
            isInvalid={showErrors && !value.mode}
            onSelect={() => onChange({ ...value, mode: "now", risks: rows })}
          />
          <ChoiceCard
            icon={ClockFastForward}
            title="Skip for now"
            description="Manually review every risk. You can configure actions later in the app."
            selected={value.mode === "skip"}
            isInvalid={showErrors && !value.mode}
            onSelect={() => onChange({ ...value, mode: "skip" })}
          />
        </div>
      </div>

      {value.mode === "skip" && (
        <div className="flex items-start gap-2.5 rounded-lg bg-unbox-cream-25 px-4 py-3.5">
          <InfoCircle className="mt-0.5 size-4 shrink-0 text-gray-500" />
          <p className="text-sm text-tertiary">
            <span className="font-semibold text-primary">Every detected risk will be sent for manual review.</span>{" "}
            Nothing is actioned automatically. You can set actions per risk later in the app.
          </p>
        </div>
      )}

      {value.mode === "now" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 max-w-xl">
            <h2 className="text-md font-semibold text-primary">Risk actions</h2>
            <p className="text-sm text-tertiary">
              Showing the {groups.reduce((sum, group) => sum + group.items.length, 0)} risks you're detecting. Choose
              what happens when each one is detected, and what to do if it's confirmed.
            </p>
          </div>
          {groups.map((group) => {
            const Icon = GROUP_ICONS[group.id];
            return (
              <div key={group.id} className="overflow-hidden rounded-xl border border-secondary bg-white">
                <div className="flex items-center justify-between border-b border-secondary px-5 py-3.5">
                  <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                    {Icon && <Icon className="size-4 text-secondary" aria-hidden="true" />}
                    {group.name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {group.items.length} {group.items.length === 1 ? "risk" : "risks"}
                  </span>
                </div>
                {group.items.map((item) => {
                  const row = byRiskId.get(item.key);
                  if (!row) return null;
                  return (
                    <div
                      key={item.key}
                      className="grid grid-cols-1 md:grid-cols-[1fr_240px_240px] items-center gap-4 border-b border-secondary px-5 py-4 last:border-b-0"
                    >
                      <div className="flex flex-col gap-0.5">
                        <p className="text-sm font-semibold text-primary">{item.name}</p>
                        {group.id !== "baseline" && <p className="text-sm text-tertiary">{item.desc}</p>}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          When risk is detected
                        </span>
                        <DetectedToggle
                          autoConfirm={row.autoConfirm}
                          onChange={(autoConfirm) => updateItem(item, { autoConfirm })}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          If risk is confirmed
                        </span>
                        <PolicyPicker
                          value={row.safetyPolicyType}
                          onChange={(safetyPolicyType) => updateItem(item, { safetyPolicyType })}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
